import { db } from "@/services/db";
import { firestore } from "@/services/firestore";
import { FeatureFlag, FeatureFlags } from "@/types/v1";
import { DocumentReference, doc, updateDoc } from "firebase/firestore";
import { store } from "@/stores/useStore";
import { FEATURE_FLAGS } from "@/types/v2/FeatureFlag";
import { Features } from "@/types/v2/Shop";

export const FEATURE_FLAG = FEATURE_FLAGS;

export type FEATURE_FLAG = (typeof FEATURE_FLAG)[keyof typeof FEATURE_FLAG];

type FEATURE_FLAG_OPTIONS = {
  [key in FEATURE_FLAG]: {
    label: string;
    canEnableGlobally: boolean;
    canEnablePerUser: boolean;
    canEnableTestUsers: boolean;
    obsolete?: boolean;
    canDisable?: boolean;
    mutuallyExclusiveWith?: FEATURE_FLAG;
    exclusiveWith?: FEATURE_FLAG;
    shopDocScoped?: boolean;
  };
};
export const FEATURE_FLAG_OPTIONS: FEATURE_FLAG_OPTIONS = {
  [FEATURE_FLAG.isCardColorEnabled]: {
    label: "Card Color",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isCardLaborEnabled]: {
    label: "Card Labor",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isCardPrimaryLinkEnabled]: {
    label: "Card Primary Link",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isPromiseTimeEnabled]: {
    label: "Promise Time",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
    shopDocScoped: true,
  },
  [FEATURE_FLAG.isServiceAdvisorFilterEnabled]: {
    label: "Service Advisor Filter",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isTechnicianFilterEnabled]: {
    label: "Technician Filter",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isSyncV2Enabled]: {
    label: "Sync V2",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
    canDisable: false,
    obsolete: true,
  },
  [FEATURE_FLAG.isTechnicianLinkEnabled]: {
    label: "Technician Container Linking",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isROLabelLinkEnabled]: {
    label: "Repair Order Label Linking",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isROLabelWritebackEnabled]: {
    label: "Repair Order Label Write-Back (Dashboard → Tekmetric)",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isTechnicianWritebackEnabled]: {
    label: "Technician Write-Back (Dashboard → Tekmetric)",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isCardColorSyncEnabled]: {
    label: "Syncing Card Color",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isManualCardOnlyEnabled]: {
    label: "Manual Card Only",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
    // mutuallyExclusiveWith: FEATURE_FLAG.isManualCardsEnabled,
    exclusiveWith: FEATURE_FLAG.isManualCardsEnabled,
  },
  [FEATURE_FLAG.isManualCardsEnabled]: {
    label: "Manual Cards (with Integration)",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
    // mutuallyExclusiveWith: FEATURE_FLAG.isManualCardOnlyEnabled,
    exclusiveWith: FEATURE_FLAG.isManualCardOnlyEnabled,
  },
  [FEATURE_FLAG.isCardNotesEnabled]: {
    label: "Card Notes",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isCustomerUpdatesEnabled]: {
    label: "Customer Updates",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isPerRoSyncEnabled]: {
    label: "Per-RO Sync",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isMetricsEnabled]: {
    label: "Metrics",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isDispatchViewEnabled]: {
    label: "Dispatch View",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
    shopDocScoped: true,
  },
  [FEATURE_FLAG.isContainerMetricsEnabled]: {
    label: "Container Metrics",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isTasksTabEnabled]: {
    label: "Tasks Tab",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
    shopDocScoped: true,
  },
  [FEATURE_FLAG.isScheduledAutomationsEnabled]: {
    label: "Scheduled Automations",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isForceCardEffectsEnabled]: {
    label: "Force Card Effects",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isAttachmentsEnabled]: {
    label: "Card & Task Attachments",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
  [FEATURE_FLAG.isBulkLinkEditEnabled]: {
    label: "Bulk Link Editing",
    canEnableGlobally: true,
    canEnablePerUser: true,
    canEnableTestUsers: true,
  },
};

export const isShopDocScopedFlag = (ff: FEATURE_FLAG): boolean =>
  !!FEATURE_FLAG_OPTIONS[ff]?.shopDocScoped;

export const getShopDocScopedValue = (
  ff: FEATURE_FLAG,
  shopEnabledFeatures: Features | undefined
): boolean => shopEnabledFeatures?.[ff] ?? false;

export const buildShopDocScopedUpdate = (
  ff: FEATURE_FLAG,
  enabled: boolean,
  shopEnabledFeatures: Features | undefined
): Features => ({
  ...shopEnabledFeatures,
  [ff]: enabled,
});

const featureFlags = {
  create: (ff: string) => {
    const docRef = doc(
      db,
      "config/featureFlags"
    ) as DocumentReference<FeatureFlags>;
    return updateDoc(docRef, {
      [ff]: {
        allUsers: false,
        testUsers: [],
      },
    });
  },
  get: (ff: string) => {
    return firestore
      .getDocument<FeatureFlags>("config/featureFlags")
      .then((res) => (res ? res[ff] : undefined));
  },
  update: (ff: string, data: FeatureFlag) => {
    const docRef = doc(
      db,
      "config/featureFlags"
    ) as DocumentReference<FeatureFlags>;
    return updateDoc(docRef, {
      [ff]: data,
    }).then(() => store.getState().setFeatureFlags());
  },
  list: () => {
    return firestore.getDocument<FeatureFlags>("config/featureFlags");
  },
};

export { featureFlags };
